import React, { useState, useEffect } from 'react';
import { Input } from 'antd';
import './CreditCard.css';

/** Kart üstünde: "yav** ***man" gibi kısmi maske (adın başı + soyadın sonu) */
const maskCardHolderPreview = (name: string): string => {
  const t = name.trim();
  if (!t) return 'AD SOYAD';
  const words = t.split(/\s+/).filter(Boolean);
  const allStars = (w: string) => '*'.repeat(w.length);

  const firstWord = (w: string) => {
    if (w.length <= 3) return allStars(w);
    return w.slice(0, 3).toLowerCase() + '*'.repeat(w.length - 3);
  };

  const lastWord = (w: string) => {
    if (w.length <= 3) return allStars(w);
    return '*'.repeat(w.length - 3) + w.slice(-3).toLowerCase();
  };

  if (words.length === 1) {
    return firstWord(words[0]);
  }
  if (words.length === 2) {
    return `${firstWord(words[0])} ${lastWord(words[1])}`;
  }
  return [firstWord(words[0]), ...words.slice(1, -1).map(allStars), lastWord(words[words.length - 1])].join(' ');
};

interface CreditCardProps {
  cardNumber: string;
  cardHolderName: string;
  expiryDate: string;
  cvv: string;
  onCardNumberChange: (value: string) => void;
  onCardHolderChange: (value: string) => void;
  onExpiryChange: (value: string) => void;
  onCvvChange: (value: string) => void;
  onCvvFocus: () => void;
  onCvvBlur: () => void;
}

const CreditCard: React.FC<CreditCardProps> = ({
  cardNumber,
  cardHolderName,
  expiryDate,
  cvv,
  onCardNumberChange,
  onCardHolderChange,
  onExpiryChange,
  onCvvChange,
  onCvvFocus,
  onCvvBlur,
}) => {
  const [isFlipped, setIsFlipped] = useState(false);
  const [cardType, setCardType] = useState<string>('default');

  // Kart numarasına göre kart tipini belirle
  useEffect(() => {
    const number = cardNumber.replace(/\s/g, '');
    if (number.startsWith('4')) {
      setCardType('visa');
    } else if (number.startsWith('5') || number.startsWith('2')) {
      setCardType('mastercard');
    } else if (number.startsWith('3')) {
      setCardType('amex');
    } else if (number.startsWith('6')) {
      setCardType('discover');
    } else {
      setCardType('default');
    }
  }, [cardNumber]);

  // Kart numarasını formatla (4'er 4'er)
  const formatCardNumber = (value: string) => {
    const cleaned = value.replace(/\s/g, '');
    const formatted = cleaned.match(/.{1,4}/g)?.join(' ') || cleaned;
    return formatted;
  };

  // Son kullanma tarihini formatla (MM/YY)
  const formatExpiry = (value: string) => {
    const cleaned = value.replace(/\D/g, '');
    if (cleaned.length >= 2) {
      return cleaned.slice(0, 2) + '/' + cleaned.slice(2, 4);
    }
    return cleaned;
  };

  // Kart numarasını maskele (sadece son 4 haneyi göster)
  const maskCardNumber = (value: string) => {
    const cleaned = value.replace(/\s/g, '');
    if (cleaned.length <= 4) return cleaned;
    const last4 = cleaned.slice(-4);
    const masked = '•'.repeat(Math.max(0, cleaned.length - 4));
    return formatCardNumber(masked + last4);
  };

  const previewHolder = maskCardHolderPreview(cardHolderName);

  // Önizlemede son kullanma gerçek rakam gösterme
  const previewExpiry = expiryDate.replace(/\D/g, '').length > 0 ? '••/••' : 'MM/YY';

  const handleCardNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 16);
    const formatted = formatCardNumber(value);
    onCardNumberChange(formatted);
  };

  const handleExpiryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const formatted = formatExpiry(value);
    onExpiryChange(formatted);
  };

  const handleCvvFocus = () => {
    setIsFlipped(true);
    onCvvFocus();
  };

  const handleCvvBlur = () => {
    setIsFlipped(false);
    onCvvBlur();
  };

  return (
    <div className="credit-card-container">
      <div className={`credit-card ${isFlipped ? 'flipped' : ''} ${cardType}`}>
        <div className="credit-card-front">
          <div className="credit-card-header">
            <div className="chip"></div>
            <div className="card-logo">
              {cardType === 'visa' && (
                <div className="visa-logo">VISA</div>
              )}
              {cardType === 'mastercard' && (
                <div className="mastercard-logo">
                  <div className="mc-circle mc-circle-1"></div>
                  <div className="mc-circle mc-circle-2"></div>
                </div>
              )}
              {cardType === 'amex' && (
                <div className="amex-logo">AMEX</div>
              )}
              {cardType === 'discover' && (
                <div className="discover-logo">DISCOVER</div>
              )}
            </div>
          </div>
          <div className="credit-card-number">
            {cardNumber ? maskCardNumber(cardNumber) : '•••• •••• •••• ••••'}
          </div>
          <div className="credit-card-footer">
            <div className="credit-card-holder">
              <div className="label">KART SAHİBİ</div>
              <div className="value">{previewHolder}</div>
            </div>
            <div className="credit-card-expiry">
              <div className="label">SON KULLANMA</div>
              <div className="value">{previewExpiry}</div>
            </div>
          </div>
        </div>
        <div className="credit-card-back">
          <div className="credit-card-stripe"></div>
          <div className="credit-card-cvv">
            <div className="label">CVV</div>
            <div className="value">•••</div>
          </div>
          <div className="credit-card-footer-back">
            <div className="card-logo-back">
              {cardType === 'visa' && <div className="visa-logo">VISA</div>}
              {cardType === 'mastercard' && (
                <div className="mastercard-logo">
                  <div className="mc-circle mc-circle-1"></div>
                  <div className="mc-circle mc-circle-2"></div>
                </div>
              )}
              {cardType === 'amex' && <div className="amex-logo">AMEX</div>}
              {cardType === 'discover' && <div className="discover-logo">DISCOVER</div>}
            </div>
          </div>
        </div>
      </div>

      <div className="credit-card-form">
        <div className="form-group">
          <label>Kart Numarası</label>
          <Input
            placeholder="1234 5678 9012 3456"
            value={cardNumber}
            onChange={handleCardNumberChange}
            maxLength={19}
            className="card-input"
          />
        </div>
        <div className="form-group">
          <label>Kart Sahibi Adı</label>
          <Input
            placeholder="AD SOYAD"
            value={cardHolderName}
            onChange={(e) => onCardHolderChange(e.target.value.toUpperCase())}
            className="card-input"
          />
        </div>
        <div className="form-row">
          <div className="form-group">
            <label>Son Kullanma Tarihi</label>
            <Input
              placeholder="MM/YY"
              value={expiryDate}
              onChange={handleExpiryChange}
              maxLength={5}
              className="card-input"
            />
          </div>
          <div className="form-group">
            <label>CVV</label>
            <Input
              placeholder="123"
              value={cvv}
              onChange={(e) => onCvvChange(e.target.value.replace(/\D/g, '').slice(0, 4))}
              onFocus={handleCvvFocus}
              onBlur={handleCvvBlur}
              maxLength={4}
              type="password"
              className="card-input"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreditCard;

